import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import api, { setApiAuthHandlers } from "../services/api";

const TOKEN_STORAGE_KEY = "token";
const USER_STORAGE_KEY = "user";

const AuthContext = createContext(null);

const readStoredToken = () => localStorage.getItem(TOKEN_STORAGE_KEY);

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_STORAGE_KEY) || "null");
  } catch {
    localStorage.removeItem(USER_STORAGE_KEY);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => readStoredToken());
  const [user, setUser] = useState(() => readStoredUser());
  const [isLoading, setIsLoading] = useState(() => Boolean(readStoredToken()));

  const clearAuthState = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    setToken(null);
    setUser(null);
    setIsLoading(false);
  }, []);

  const persistAuthState = useCallback((nextToken, nextUser) => {
    if (nextToken) {
      localStorage.setItem(TOKEN_STORAGE_KEY, nextToken);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }

    if (nextUser) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }

    setToken(nextToken || null);
    setUser(nextUser || null);
  }, []);

  const login = useCallback(
    ({ token: nextToken, user: nextUser }) => {
      if (!nextToken || !nextUser) {
        throw new Error("Auth payload requires both token and user.");
      }

      persistAuthState(nextToken, nextUser);
      setIsLoading(false);
      return nextUser;
    },
    [persistAuthState],
  );

  const logout = useCallback(() => {
    clearAuthState();
  }, [clearAuthState]);

  const refreshUser = useCallback(async () => {
    const storedToken = readStoredToken();

    if (!storedToken) {
      clearAuthState();
      return null;
    }

    setIsLoading(true);

    try {
      const response = await api.get("/auth/me");
      persistAuthState(storedToken, response.data);
      return response.data;
    } catch (error) {
      clearAuthState();
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [clearAuthState, persistAuthState]);

  useEffect(() => {
    setApiAuthHandlers({
      getToken: () => readStoredToken(),
      onUnauthorized: () => clearAuthState(),
    });

    return () => {
      setApiAuthHandlers({
        getToken: () => readStoredToken(),
        onUnauthorized: undefined,
      });
    };
  }, [clearAuthState]);

  useEffect(() => {
    let isMounted = true;

    const hydrateSession = async () => {
      const storedToken = readStoredToken();

      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get("/auth/me");
        if (!isMounted) return;

        persistAuthState(storedToken, response.data);
      } catch {
        if (!isMounted) return;
        clearAuthState();
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    hydrateSession();

    return () => {
      isMounted = false;
    };
  }, [clearAuthState, persistAuthState]);

  const userPrimaryRoute = useMemo(() => {
    if (user?.role === "admin") return "/admin-dashboard";
    if (user?.role === "candidate") return "/candidate-dashboard";
    return "/dashboard";
  }, [user]);

  const value = useMemo(
    () => ({
      token,
      user,
      isLoading,
      isAuthenticated: Boolean(token && user),
      userPrimaryRoute,
      login,
      logout,
      refreshUser,
      setAuthUser: (nextUser) => persistAuthState(token, nextUser),
    }),
    [isLoading, login, logout, persistAuthState, refreshUser, token, user, userPrimaryRoute],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }

  return context;
};
